import * as Stomp from 'stompjs';
import SockJS from 'sockjs-client';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class WebsocketService {
  private stompClient: any;
  private connected = false;
  private connecting = false;

  private topicCallbacks = new Map<string, Set<(message: any) => void>>();
  private topicSubscriptions = new Map<string, any>();

  // 🔔 Subjects (same as old working code)
  private updateNotificationCountViaWebSocket = new Subject<any>();
  updateNotificationCountViaWebSocket$ =
    this.updateNotificationCountViaWebSocket.asObservable();

  private claimReceived = new Subject<any>();
  claimReceived$ = this.claimReceived.asObservable();

  private notificationReceived = new Subject<any>();
  notificationReceived$ = this.notificationReceived.asObservable();

  constructor(private authService: AuthService) {}

  /* -------------------- PRIVATE HELPERS -------------------- */

  private getHeaders() {
    const token = this.authService.getToken();

    return token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {};
  }

  private sendProfileRegistration() {
    const user = this.authService.getUser();

    if (user?.defaultProfileId && this.connected) {
      this.stompClient.send(
        '/app/notification',
        {},
        JSON.stringify({ profileId: user.defaultProfileId })
      );
    }
  }

  private createConnection() {
    if (this.connected || this.connecting) return;

    this.connecting = true;

    const socket = new SockJS(`${environment.webSocketEndPoint}ws`);
    this.stompClient = Stomp.over(socket);

    // STOMP's debugger logs complete protocol frames, including Authorization
    // headers. Keep it disabled outside of an explicitly controlled diagnostic
    // session so bearer tokens never reach the browser console.
    this.stompClient.debug = () => {};
    this.stompClient.heartbeat.outgoing = 10000;
    this.stompClient.heartbeat.incoming = 10000;

    this.stompClient.connect(
      this.getHeaders(),
      () => {
        this.connected = true;
        this.connecting = false;

        this.topicSubscriptions.clear();

        // Built-in subscriptions (from old code)
        this.subscribeInternalTopics();
        this.topicCallbacks.forEach((_, topic) => this.subscribeToTopic(topic));

        // 🔥 REQUIRED (same as old code)
        this.sendProfileRegistration();
      },
      (error: any) => {
        console.error('WebSocket Error:', error);
        this.connected = false;
        this.connecting = false;
        this.topicSubscriptions.clear();

        if (this.authService.getToken()) {
          setTimeout(() => this.createConnection(), 5000);
        }
      }
    );
  }

  private subscribeInternalTopics() {
    // /user queue
    this.stompClient.subscribe('/user/queue/messages', (msg: { body: any }) => {
      this.updateNotificationCountViaWebSocket.next(msg.body);
    });

    // Claim topic
    this.stompClient.subscribe('/topic/claim', (msg: { body: string }) => {
      this.claimReceived.next(JSON.parse(msg.body));
    });

    // Notification count
    this.stompClient.subscribe(
      '/user/queue/notificationCount',
      (msg: { body: string }) => {
        this.notificationReceived.next(JSON.parse(msg.body));
      }
    );
  }

  /* -------------------- PUBLIC API -------------------- */

  ensureConnection() {
    if (!this.connected) {
      this.createConnection();
      return;
    }

    this.sendProfileRegistration();
  }

  connect(topic: string, callback: (message: any) => void): () => void {
    let callbacks = this.topicCallbacks.get(topic);
    if (!callbacks) {
      callbacks = new Set();
      this.topicCallbacks.set(topic, callbacks);
    }
    callbacks.add(callback);

    this.ensureConnection();
    this.subscribeToTopic(topic);

    return () => this.unsubscribe(topic, callback);
  }

  private subscribeToTopic(topic: string) {
    if (!this.connected || this.topicSubscriptions.has(topic)) {
      return;
    }

    const subscription = this.stompClient.subscribe(
      topic,
      (msg: { body: string }) => {
        const message = JSON.parse(msg.body);
        this.topicCallbacks
          .get(topic)
          ?.forEach((callback) => callback(message));
      }
    );
    this.topicSubscriptions.set(topic, subscription);
  }

  private unsubscribe(topic: string, callback: (message: any) => void) {
    const callbacks = this.topicCallbacks.get(topic);
    if (!callbacks) {
      return;
    }

    callbacks.delete(callback);
    if (callbacks.size > 0) {
      return;
    }

    this.topicCallbacks.delete(topic);
    const subscription = this.topicSubscriptions.get(topic);
    if (subscription) {
      subscription.unsubscribe();
      this.topicSubscriptions.delete(topic);
    }
  }

  send(destination: string, body: any) {
    if (!this.connected) {
      console.warn('WebSocket not connected. Message not sent.');
      return;
    }
    this.stompClient.send(destination, {}, JSON.stringify(body));
  }

  disconnect() {
    if (this.stompClient && this.connected) {
      this.stompClient.disconnect(() => {
        this.connected = false;
        this.topicSubscriptions.clear();
      });
    }
  }
}
